import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { StorefrontApiError } from '../../../../lib/api/storefront-client.js';
import { fetchWishlist, addWishlistItem, removeWishlistItem, moveWishlistItemToCart } from '../api/wishlist-api.js';
import type { MoveToCartInput } from '../types/wishlist.js';

const WISHLIST_KEY = ['storefront', 'wishlist'] as const;
const CART_KEY = ['storefront', 'cart'] as const;

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new StorefrontApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

function nonRetryableStatus(error: unknown): boolean {
  return error instanceof StorefrontApiError && [401, 403, 404, 409, 422].includes(error.status);
}

/** Shared by the header's wishlist badge, the wishlist page, and any product-card "in wishlist" indicator -- same one-query-many-consumers pattern as `use-cart.ts`. */
export function useWishlist() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: WISHLIST_KEY,
    queryFn: async ({ signal }) => fetchWishlist({ token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 15_000,
    retry: (count, error) => !nonRetryableStatus(error) && count < 2,
  });
}

export function useWishlistItemCount(): number {
  const { data } = useWishlist();
  return data?.length ?? 0;
}

export function useWishlistMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: async (productId: string) => addWishlistItem(productId, { token: await requireToken(getToken) }),
    onSuccess: (items) => queryClient.setQueryData(WISHLIST_KEY, items),
  });

  const remove = useMutation({
    mutationFn: async (itemId: string) => removeWishlistItem(itemId, { token: await requireToken(getToken) }),
    onSuccess: (items) => queryClient.setQueryData(WISHLIST_KEY, items),
  });

  const moveToCart = useMutation({
    mutationFn: async ({ itemId, input }: { itemId: string; input: MoveToCartInput }) =>
      moveWishlistItemToCart(itemId, input, { token: await requireToken(getToken) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WISHLIST_KEY });
      void queryClient.invalidateQueries({ queryKey: CART_KEY });
    },
  });

  return { add, remove, moveToCart };
}
